import { analyticsService } from '../index-business';

export interface PhoneEventData {
  ownerId?: string | number;
  studioClientId?: string | number;
  pendingClientId?: string | number;
  callId?: string | number;
  internalNumber?: string;
  externalNumber?: string;
  callType?: string;
  [key: string]: any;
}

export class PhoneTracker {
  static trackGetClientPhoneCalls(data: {
    ownerId: string | number;
    studioClientId: string | number;
    success: boolean;
    error?: string;
    duration?: number;
    callsCount?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      studio_client_id: data.studioClientId,
      calls_count: data.callsCount || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getClientPhoneCalls',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Client Phone Calls Retrieved', eventData);
  }

  static trackGetPendingClientPhoneCalls(data: {
    ownerId: string | number;
    pendingClientId: string | number;
    success: boolean;
    error?: string;
    duration?: number;
    callsCount?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      pending_client_id: data.pendingClientId,
      calls_count: data.callsCount || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getPendingClientPhoneCalls',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Pending Client Phone Calls Retrieved', eventData);
  }

  static trackGetPhoneCalls(data: {
    ownerId: string | number;
    from: any;
    to: any;
    callType: string;
    success: boolean;
    error?: string;
    duration?: number;
    callsCount?: number;
  }) {
    const fromDate = data.from?.format ? data.from.format('DD-MM-YYYY') : data.from;
    const toDate = data.to?.format ? data.to.format('DD-MM-YYYY') : data.to;

    const eventData = {
      owner_id: data.ownerId,
      call_type: data.callType,
      date_from: fromDate,
      date_to: toDate,
      date_range_days:
        data.from && data.to ? Math.ceil((new Date(data.to) - new Date(data.from)) / (1000 * 60 * 60 * 24)) : 0,
      calls_count: data.callsCount || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getPhoneCalls',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Phone Calls Filtered Retrieved', eventData);
  }

  static trackGetPhoneCallRecord(data: {
    ownerId: string | number;
    callId: string | number;
    success: boolean;
    error?: string;
    duration?: number;
    hasRecording?: boolean;
    recordingDuration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      call_id: data.callId,
      has_recording: data.hasRecording || false,
      recording_duration_seconds: data.recordingDuration || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getPhoneCallRecord',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Phone Call Record Retrieved', eventData);
  }

  static trackMakeExternalCall(data: {
    ownerId: string | number;
    internalNumber: string;
    externalNumber: string;
    success: boolean;
    error?: string;
    duration?: number;
    callId?: string;
    callStatus?: string;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      internal_number: data.internalNumber,
      external_number: data.externalNumber,
      external_number_masked: data.externalNumber
        ? data.externalNumber.substring(0, 3) + '***' + data.externalNumber.substring(data.externalNumber.length - 2)
        : '',
      call_id: data.callId,
      call_status: data.callStatus,
      call_direction: 'outbound',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'makeExternalCall',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('External Call Made', eventData);
  }

  static trackPhoneActivity(data: {
    ownerId: string | number;
    activityType: 'call_initiated' | 'call_answered' | 'call_ended' | 'recording_played';
    callId?: string | number;
    callDuration?: number;
    callDirection?: 'inbound' | 'outbound';
    clientType?: 'existing' | 'pending';
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      activity_type: data.activityType,
      call_id: data.callId,
      call_duration_seconds: data.callDuration || 0,
      call_direction: data.callDirection,
      client_type: data.clientType,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'phone_activity',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Phone Activity', eventData);
  }

  static trackPhoneApiCall(
    endpoint: string,
    data: PhoneEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Phone API: ${endpoint}`, eventData);
  }
}

export const trackPhoneSuccess = (endpoint: string, data: PhoneEventData, duration?: number) => {
  PhoneTracker.trackPhoneApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackPhoneError = (endpoint: string, error: string, data: PhoneEventData, duration?: number) => {
  PhoneTracker.trackPhoneApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default PhoneTracker;
